/** @jsxImportSource @emotion/react */
'use memo'

import React from 'react'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { font, paragraphSmall } from 'src/components/css'
import { ProfilePlayer } from '../../types'
import PlayerSwitchRow from './PlayerSwitchRow'

// SECONDARY_125 (#3a4659) exists at runtime but isn't in the inferred `colors`
// type (untyped appColors.js); read it via a cast, matching existing usage.
const BORDER_COLOR = (colors as Record<string, string>).SECONDARY_125

interface PlayerSwitchPanelProps {
  players: ProfilePlayer[]
  selectedPlayerId: string | null
  onSelect: (player: ProfilePlayer) => void
  /** Called after a row is chosen so the parent can close the dropdown. */
  onClose: () => void
}

/**
 * The player-switch dropdown panel. Rendered as antd Dropdown's `dropdownRender`
 * content, anchored under the player-name header. Scrolls when the user has many
 * players; rows are divided by a thin line.
 */
const PlayerSwitchPanel: React.FC<PlayerSwitchPanelProps> = ({
  players,
  selectedPlayerId,
  onSelect,
  onClose,
}) => {
  if (players.length === 0) {
    return (
      <div css={panelStyle}>
        <p css={emptyStyle}>No players</p>
      </div>
    )
  }

  return (
    <div css={panelStyle}>
      <div css={listStyle}>
        {players.map((p, idx) => {
          // The divider sits between players[idx-1] and players[idx]. Keep it in
          // the layout (preserving spacing) but render it transparent when
          // either neighbour is the selected row, so its highlight band isn't
          // bracketed by a visible line above/below.
          const isSelected = String(p.id) === selectedPlayerId
          const prevSelected =
            idx > 0 && String(players[idx - 1].id) === selectedPlayerId
          const dividerHidden = isSelected || prevSelected

          return (
            <React.Fragment key={String(p.id)}>
              {idx > 0 && (
                <div
                  css={[dividerStyle, dividerHidden && dividerHiddenStyle]}
                />
              )}
              <PlayerSwitchRow
                player={p}
                isSelected={isSelected}
                onSelect={() => {
                  onSelect(p)
                  onClose()
                }}
              />
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}

export default PlayerSwitchPanel

// ─── Styles ──────────────────────────────────────────────────────────────────

const panelStyle = css`
  background-color: ${colors.SECONDARY_300};
  border: 1px solid ${BORDER_COLOR};
  border-radius: 8px;
  /* Only vertical padding — rows span the full width so their highlight reaches
     the panel's side edges. overflow:hidden clips the row band to the rounded
     corners. */
  padding: 8px 0;
  overflow: hidden;
  box-shadow: 0 6px 16px rgba(6, 10, 16, 0.4);
`

// One row ≈ 54px tall (content + 9px padding top/bottom) + a 1px divider. Cap the
// visible list to four whole rows (4*54 + 3*1 ≈ 219px) so the bottom never shows
// a half-cut row; the rest scrolls.
const listStyle = css`
  display: flex;
  flex-direction: column;
  max-height: 219px;
  min-width: 270px;
  overflow-y: auto;

  /* Thin scrollbar overlaid on the right edge. */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 2px 0;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${BORDER_COLOR};
    border-radius: 8px;
  }
  scrollbar-width: thin;
  scrollbar-color: ${BORDER_COLOR} transparent;
`

// A hairline that sits flush between two row bands — no vertical margin, so the
// inter-row spacing (owned by the rows' padding) is covered by a row's
// selected/hover highlight. A small 4px horizontal inset keeps it just shy of
// the panel edges.
const dividerStyle = css`
  height: 1px;
  flex-shrink: 0;
  background-color: ${colors.SOFT_STEEL};
  margin: 0 4px;
`

// Keep the divider's footprint (spacing) but hide the line next to the selected
// row so its highlight band isn't bracketed.
const dividerHiddenStyle = css`
  background-color: transparent;
`

const emptyStyle = css`
  ${font.body}
  ${paragraphSmall}
  color: ${colors.NEUTRAL_50};
  text-align: center;
  padding: 16px 0;
  margin: 0;
`
